/* eslint-disable */
import Toggler from "components/common/toggler/Toggler";
import "./metric-helpers/rawChartComponent.scss";
import MainTooltip from "components/common/mainTooltip/MainTooltip";
import { useEffect, useState } from "react";
import {
  DARK_MODE_CHART_AVERAGE_LINE_1,
  DARK_MODE_CHART_AXIS_1,
  DARK_MODE_CHART_GRID_1,
  DARK_MODE_CHART_TICK_1,
  LIGHT_MODE_CHART_AVERAGE_LINE_1,
  LIGHT_MODE_CHART_AXIS_1,
  LIGHT_MODE_CHART_GRID_1,
  LIGHT_MODE_CHART_TICK_1,
  NEW_AXIS_COLOR,
  NEW_AXIS_TICK,
  NEW_BOTH_FEET_COLOR,
  NEW_LEFT_COLOR,
  NEW_LEFT_STANCE_COLOR,
  NEW_LEFT_SWING_COLOR,
  NEW_MID_COLOR,
  NEW_RIGHT_COLOR,
  NEW_RIGHT_STANCE_COLOR,
  NEW_RIGHT_SWING_COLOR,
} from "utils/constant";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  Tooltip,
  YAxis,
  ReferenceLine,
  Bar,
  Cell,
  Legend,
  BarChart,
  PieChart,
  Pie,
} from "recharts";
import Polygon from "components/common/icons/downArrow";
import MaxMinMean from "./metric-helpers/MaxMinMean";
import mappingTrimAreaX from "./metric-helpers/mappingTrimArea";
import mappingTrimAreaY from "./metric-helpers/mappingTrimAreaY";
import { getTooltipStyle } from "utils/metrics";
import { nanoid } from "@reduxjs/toolkit";
import COPImagesComponent from "components/copImagesComponent/COPImagesComponent";
import { e } from "mathjs";
import { useSelector } from "react-redux";

const formatMinutes = (value) => {
  return ((value % (60 * 60 * 1000)) / 60).toFixed(1);
};

const formatTime = (value, data) => {
  if (data?.xAxisValue >= 300) {
    // If the value is over 5 minutes, use minutes format
    return formatMinutes(value);
  } else {
    // Otherwise, use seconds format
    return value;
  }
};

const modifyTimeString = (timeStr, value) => {
  if (value > 300) {
    return timeStr?.replace("(s)", "(min)"); // Replace "(s)" with "(m)"
  } else {
    return timeStr; // Return original string if <= 400
  }
};

const chartXdomainScale = (arr) => {
  let newArr = arr?.map((item) => (item < 0 ? item * -1 : item));

  //custom range
  // return([-1*(Math.max(...newArr))-10, Math.max(...newArr)+10])
  if (newArr) {
    return Math.max(...newArr) < 50 ? [-50, 50] : [-100, 100];
  }
};

const combineArraysByXAxisValue = (...arrays) => {
  const combinedMap = new Map();
  const allKeys = new Set();

  // First pass: Combine objects and gather all possible keys
  arrays?.forEach((array) => {
    array?.forEach((item) => {
      if (!item || typeof item !== "object") return; // Skip if item is undefined or not an object

      const { xAxisValue, ...rest } = item;

      // Add all keys to the allKeys set
      Object.keys(rest).forEach((key) => allKeys.add(key));

      if (combinedMap.has(xAxisValue)) {
        combinedMap.set(xAxisValue, {
          ...combinedMap.get(xAxisValue),
          ...rest,
        });
      } else {
        combinedMap.set(xAxisValue, item);
      }
    });
  });

  // Second pass: Add nulls for missing keys
  combinedMap.forEach((value, key) => {
    allKeys.forEach((k) => {
      if (!(k in value)) {
        value[k] = null;
      }
    });
    combinedMap.set(key, value);
  });

  // Return the sorted array

  return Array.from(combinedMap.values()).sort(
    (a, b) => a.xAxisValue - b.xAxisValue
  );
};

//preparing data for metric
const prepareDataForMetric = (
  dataObject,
  bilateral,
  hasBilateralAsymmetry,
  hasAsymmetry,
  mainDataKeys,
  secondaryDataKeys,
  asymmetryDataKeys,
  bilateralAsymmetryKeys,
  metricPrimaryKey,
  xAxisValue,
  metricSecondaryKey,
  toggleGraph,
  isJump = false
) => {
  // Helper function to map data with the appropriate key
  const mapData = (dataKey, dataTypeKey) =>
    dataObject?.[dataKey]?.map((item, index) => ({
      xAxisValue: xAxisValue === "rep" ? index + 1 : item?.[xAxisValue],
      timeOfStep: item?.timeOfStep,
      eventStart: item?.eventStart,
      eventEnd: item?.eventEnd,
      [dataTypeKey]:
        item?.[toggleGraph ? metricSecondaryKey : metricPrimaryKey],
    })) || [];

  // Handling bilateral data (left, right, and possibly mean)
  const processBilateralData = () => {
    const leftData = mapData(mainDataKeys[0], "metricLeftValue");
    const rightData = mapData(mainDataKeys[1], "metricRightValue");
    const meanData = isJump
      ? mapData(secondaryDataKeys, "metricMeanValue")
      : [];

    let asymmetryData = [];
    if (hasAsymmetry || hasBilateralAsymmetry) {
      const asymmetryKey = asymmetryDataKeys || bilateralAsymmetryKeys;
      asymmetryData = mapData(asymmetryKey, "metricAsymmetryValue");
    }

    return combineArraysByXAxisValue(
      leftData,
      rightData,
      meanData,
      asymmetryData
    );
  };

  // Handling non-bilateral data (mean and possibly asymmetry)
  const processNonBilateralData = () => {
    const meanData = isJump ? mapData(mainDataKeys[0], "metricMeanValue") : [];

    let asymmetryData = [];
    if (hasAsymmetry || hasBilateralAsymmetry) {
      const asymmetryKey = asymmetryDataKeys || bilateralAsymmetryKeys;
      asymmetryData = mapData(asymmetryKey, "metricAsymmetryValue");
    }

    return asymmetryData.length
      ? combineArraysByXAxisValue(meanData, asymmetryData)
      : meanData;
  };

  // Choose the appropriate processing function
  return bilateral ? processBilateralData() : processNonBilateralData();
};

//combined metrics for drive index charts as well as duty factor
const preparedDataForCombinedMetric = (
  data,
  hasCombinedKeys,
  metricPrimaryKey,
  combinedMainKeys,
  mainDataKeys,
  asymmetryDataKeys
) => {
  // Utility function to map data
  const mapData = (dataKeys, dataKey, keyToExtract) =>
    dataKeys?.flatMap((foot) =>
      data?.[foot]?.map((item) => ({
        [dataKey]: item?.[keyToExtract],
        xAxisValue: item?.timeOfStep,
        timeOfStep: item?.timeOfStep,
      }))
    ) || [];

  let mainData = [];
  let secondaryData = [];
  let tertiaryData = [];
  let asymmetryData = [];

  if (hasCombinedKeys) {
    if (metricPrimaryKey) {
      mainData = mapData(mainDataKeys, metricPrimaryKey, metricPrimaryKey);
    }
    if (combinedMainKeys?.[0]) {
      secondaryData = mapData(
        mainDataKeys,
        combinedMainKeys[0],
        combinedMainKeys[0]
      );
    }
    if (combinedMainKeys?.[1]) {
      tertiaryData = mapData(
        mainDataKeys,
        combinedMainKeys[1],
        combinedMainKeys[1]
      );
    }
    if (metricPrimaryKey) {
      asymmetryData = mapData(
        asymmetryDataKeys,
        asymmetryDataKeys[0],
        combinedMainKeys[0]
      );
    }
  } else {
    if (metricPrimaryKey) {
      mainData = mapData(mainDataKeys, metricPrimaryKey, metricPrimaryKey);
      asymmetryData = mapData(
        asymmetryDataKeys,
        asymmetryDataKeys[0],
        metricPrimaryKey
      );
    }
  }

  const combinedValues = combineArraysByXAxisValue(
    mainData,
    secondaryData,
    tertiaryData,
    asymmetryData
  );

  return combinedValues;
};

//prepare data for bullet charts
const perpareDataForBulletChart = (
  data,
  mainKey,
  hasCards,
  cardKeys,
  dataKeys,
  secondaryDataKeys
) => {
  const combineObjects = (dataArray) => {
    const result = {};

    dataArray.forEach((item) => {
      const key = Object.keys(item)[0]; // Get the key of the current object
      result[key] = item[key]; // Directly assign the array of values to the corresponding key
    });

    return result;
  };

  const combineArrayValues = (inputObject) => {
    const result = {};

    Object.keys(inputObject).forEach((key) => {
      const combinedObject = {};

      inputObject[key].forEach((item) => {
        Object.assign(combinedObject, item); // Combine all properties of the array objects into one
      });

      result[key] = combinedObject;
    });

    return result;
  };

  //bullet data
  let bulletData = [];

  const allDataKeys = [...dataKeys, ...secondaryDataKeys];

  let mainData = allDataKeys?.map((item) => ({
    [item]: data?.[item]?.[0]?.[mainKey],
  }));

  let cardData = cardKeys?.map((card) => ({
    [card]: allDataKeys?.map((item) => ({
      [item]: data?.[item]?.[0]?.[card],
    })),
  }));

  bulletData = {
    [mainKey]: mainData,
    ...combineObjects(cardData),
  };

  return combineArrayValues(bulletData);
};

// Pie chart data preparation
const prepareDataForPieMetric = (
  dataObject,
  mainDataKeys,
  metricPrimaryKey,
  metricSecondaryKey
) => {
  // Helper function to calculate the average value of a metric
  const calculateAverage = (data, key) => {
    const values = data?.map((item) => item?.[key]).filter((item) => item);
    return values?.reduce((a, b) => a + b, 0) / values?.length || 0;
  };

  // Process left and right data using the helper function
  const processData = (key) => ({
    stance: calculateAverage(dataObject?.[mainDataKeys[0]], key),
    swing: calculateAverage(dataObject?.[mainDataKeys[1]], key),
  });

  // Compute values for left and right
  const leftData = processData(metricPrimaryKey);
  const rightData = processData(metricSecondaryKey);

  return {
    left: [
      { name: "Stance", value: leftData.stance },
      { name: "Swing", value: rightData.stance },
    ],
    right: [
      { name: "Stance", value: leftData.swing },
      { name: "Swing", value: rightData.swing },
    ],
  };
};

//render pie chart
const renderPieChart = (pieData, pieHeading, foot) => {
  const LEFT_COLORS = [NEW_LEFT_STANCE_COLOR, NEW_LEFT_SWING_COLOR];
  const RIGHT_COLORS = [NEW_RIGHT_STANCE_COLOR, NEW_RIGHT_SWING_COLOR];
  const RADIAN = Math.PI / 180;

  //custom tooltip for pie
  const CustomTooltipPie = ({ active, payload, data }) => {
    if (active && payload && payload.length) {
      let total = data[0].value + data[1].value;
      let percentage = ((payload[0].value / total) * 100).toFixed(2);

      return (
        <div className="custom-tooltip">
          <div className="label">
            {`${payload[0].name}`} : {` ${percentage}`}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCustomizedLabel = (payload) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = payload;

    // * moves the lables either closer or further from the center of the piechart
    const radius = innerRadius + (outerRadius - innerRadius) * -1.1;
    let x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (x > cx) {
      x = x - 15;
    } else {
      x = x + 15;
    }
    return (
      <text
        fontSize={16}
        fontWeight={"bold"}
        x={x}
        y={y}
        fill={payload.fill}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="metric_pie_chart">
      <div className="chart-container-header">
        {pieHeading}{" "}
        {/* <MainTooltip
          tooltipText={
            pieMetricDescription + ". " + pieSecondaryMetricDescription
          }
        /> */}
      </div>
      <div className="metric_pie_chart_container">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              dataKey="value"
              data={pieData}
              cx="50%"
              cy="50%"
              // innerRadius={70}
              // outerRadius={95}
              innerRadius="70%"
              outerRadius="95%"
              labelLine={false}
              label={renderCustomizedLabel}
              paddingAngle={10}
              stroke="none"
              opacity={0.8}
            >
              {pieData &&
                Object.keys(pieData).length &&
                pieData.map((entry, index) =>
                  foot === "Left" ? (
                    <Cell
                      key={`cell-${index}`}
                      fill={LEFT_COLORS[index % LEFT_COLORS.length]}
                    />
                  ) : (
                    <Cell
                      key={`cell-${index}`}
                      fill={RIGHT_COLORS[index % RIGHT_COLORS.length]}
                    />
                  )
                )}
            </Pie>
            <Tooltip content={<CustomTooltipPie data={pieData} />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

//custom tooltip
const CustomTooltipForJumpCharts = ({ active, payload, label, data, desc }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        {data[label - 1] && (
          <div className="label">Rep {`${data[label - 1].xAxisValue}`}</div>
        )}

        {payload[0] && payload[0].name === "Both Feet" && (
          <div className="both-feet">
            {`${payload[0].name} `} :{" "}
            {` ${payload[0].value ? payload[0].value : "N/A "}`}
            {desc}
          </div>
        )}
        {payload.length >= 1 && payload[0].name !== "Both Feet" && (
          <div className={`${getTooltipStyle(payload[0])}`}>
            {`${payload[0].name} `} :{" "}
            {` ${payload[0].value ? payload[0].value : "N/A "}`}
            {desc}
          </div>
        )}
        {payload.length >= 2 && payload[1].name !== "Both Feet" && (
          <div className={`${getTooltipStyle(payload[1])}`}>
            {`${payload[1].name} `} :{" "}
            {` ${payload[1].value ? payload[1].value : "N/A "}`}
            {desc}
          </div>
        )}
      </div>
    );
  }

  return null;
};

//check if any of the of the data has useable values
const checkKeysForTruthyValues = (
  arrayOfObjects,
  key1,
  key2,
  key3,
  key4,
  key5
) => {
  // Ensure arrayOfObjects is an array, otherwise return false
  if (!Array.isArray(arrayOfObjects)) {
    //console.log("The first argument must be an array of objects.");
    return false;
  }

  // Ensure all elements in arrayOfObjects are objects
  for (const obj of arrayOfObjects) {
    if (typeof obj !== "object" || obj === null) {
      //console.log("The array must contain objects only.");
      return false;
    }
  }

  // Check if any object in the array has truthy values for key1 or key2
  return arrayOfObjects.some(
    (obj) =>
      Boolean(obj[key1]) ||
      Boolean(obj[key2]) ||
      Boolean(obj[key3]) ||
      Boolean(obj[key4]) ||
      Boolean(obj[key5])
  );
};

//custom bar
const CustomBar = (props) => {
  const { fill, x, y, width, height, stroke } = props;

  const alteredWidth =
    (width / 10) * props?.payload?.value[props?.payload.valueKey];

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={alteredWidth}
        height={height}
        fill={fill}
        stroke="none"
        rx={15} // Border radius for the inner rectangle
        ry={15} // Border radius for the inner rectangle
      />

      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="none"
        stroke={stroke}
        strokeWidth={1} // Adjust the stroke width as needed
        rx={15} // Border radius for the outer rectangle
        ry={15} // Border radius for the outer rectangle
      />
    </g>
  );
};

const RawChartComponent = (props) => {
  const {
    key,
    metric,
    sessionMetricData,
    metricName,
    metricPrimaryGraphName,
    metricID,
    metricSecondaryGraphName,
    metricPrimaryKey,
    metricSecondaryKey,
    metricTertiaryKey,
    metricPrimaryName,
    metricSecondaryName,
    metricTertiaryGraphName,
    metricUnit,
    metricSecondaryUnit,
    metricPrimaryDescription,
    metricSecondaryDescription,
    metricChartType,
    toggleableMetric,
    toggleOptions,
    xAxisValue,
    xAxisSecondaryValue,
    xAxisLabel,
    xAxisSecondaryLabel,
    yAxisLabel,
    yAxisSecondaryLabel,
    inDevelopment,
    bilateral,
    hasBilateralToggler,
    bilateralToggleOptions,
    hasBilateralAsymmetry,
    bilateralAsymmetryKeys,
    hasCombinedKeys,
    mainDataKeys,
    secondaryDataKeys,
    combinedMainKeys,
    combinedDataKeys,
    hasAsymmetry,
    asymmetryDataKeys,
    forDevOnly,
    graphDomain,
    trimDomain,
    isTrimOpen,
    metricSecondaryMetricName,
    metricTertiaryMetricName,
    isJump,
    isSuperAdmin,
    getSucceededData,
    hasCards,
    cardKeys,
    cardHeadings,
    cardUnits,
    imageHeadings,
    imageDataKeys,
    imageTooltips,
    disabledGraphIDs,
    selectedActivityType,
    hasMultipleActivities,
  } = props;

  const [toggleGraph, setToggleGraph] = useState(false);
  const [toggleBilateralGraph, setToggleBailateralGraph] = useState(false);
  const [graphData, setGraphData] = useState([]);


  function filterByTimeRange(numericValues, dataArray, unset) {
    if (unset) {
    }
  
    if (!numericValues) {
      return dataArray; // Return the original array if numericValues is false
    }
  
    const [min, max] = numericValues;
  
    const filteredData = dataArray?.filter((item) => {
      if (item.eventStart !== undefined && item.eventEnd !== undefined) {
        // Prioritize eventStart and eventEnd if present
        return item.eventStart >= min && item.eventEnd <= max;
      }
  
      // Otherwise, fallback to timeOfStep filtering
      return item.timeOfStep >= min && item.timeOfStep <= max;
    });

  
    // Sort the filtered data by xAxisValue //remapping xAxis for sessions with rep categories
    return filteredData?.sort((a, b) => a.xAxisValue - b.xAxisValue).map((item, index) => ({...item, xAxisValueCat: index+1}));
  }

  const data =
    getSucceededData(sessionMetricData)?.raw ||
    getSucceededData(sessionMetricData)?.rawData;

  const fullData = getSucceededData(sessionMetricData);

  const mode = useSelector((state) => state.theme.mode);

  const tickColor =
    mode === "dark" ? DARK_MODE_CHART_TICK_1 : LIGHT_MODE_CHART_TICK_1;
  const gridColor =
    mode === "dark" ? DARK_MODE_CHART_GRID_1 : LIGHT_MODE_CHART_GRID_1;
  const axisColor =
    mode === "dark" ? DARK_MODE_CHART_AXIS_1 : LIGHT_MODE_CHART_AXIS_1;
  const avgColor =
    mode === "dark"
      ? DARK_MODE_CHART_AVERAGE_LINE_1
      : LIGHT_MODE_CHART_AVERAGE_LINE_1;

  //variable for asymmetry gradient
  let offset;
  let asymmID = nanoid();

  //chart asymmetry toggle state
  const [asymmetryToggled, setAsymmetryToggled] = useState(true);

  //variables for max min avg
  const leftFootData =
    metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" &&
    Array.isArray(graphData) // Check if graphData is an array
      ? graphData
          ?.filter((item) =>
            graphDomain
              ? item.eventStart >= graphDomain[0] &&
                item.eventEnd <= graphDomain[1]
              : true
          )
          ?.map((item) => item.metricLeftValue)
          ?.filter((item) => item !== null && item !== undefined)
      : [];
  // graphData?.filter(item => graphDomain ? item.xAxisValue >= graphDomain[0] && item.xAxisValue <= graphDomain[1] : item)
  //   ?.map((item) => item.metricLeftValue)
  //   .filter((item) => item !== null && item !== undefined);
  const rightFootData =
    metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" &&
    Array.isArray(graphData) // Check if graphData is an array
      ? graphData
          ?.filter((item) =>
            graphDomain
              ? item.eventStart >= graphDomain[0] &&
                item.eventEnd <= graphDomain[1]
              : true
          )
          ?.map((item) => item.metricRightValue)
          ?.filter((item) => item !== null && item !== undefined)
      : [];
  // graphData?.filter(item => graphDomain ? item.xAxisValue >= graphDomain[0] && item.xAxisValue <= graphDomain[1] : item)
  //   ?.map((item) => item.metricRightValue)
  //   .filter((item) => item !== null && item !== undefined);
  const meanFootData =
    metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" &&
    Array.isArray(graphData) // Check if graphData is an array
      ? graphData
          ?.filter((item) =>
            graphDomain
              ? item.eventStart >= graphDomain[0] &&
                item.eventEnd <= graphDomain[1]
              : true
          )
          ?.map((item) => item.metricMeanValue)
          ?.filter((item) => item !== null && item !== undefined)
      : [];

  // graphData?.filter(item => graphDomain ? item.xAxisValue >= graphDomain[0] && item.xAxisValue <= graphDomain[1] : item)
  //   ?.map((item) => item.metricMeanValue)
  //   .filter((item) => item !== null && item !== undefined);
  const symmetryFootData =
    metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" &&
    Array.isArray(graphData) // Check if graphData is an array
      ? graphData
          ?.filter((item) =>
            graphDomain
              ? item.eventStart >= graphDomain[0] &&
                item.eventEnd <= graphDomain[1]
              : true
          )
          ?.map((item) => item.metricAsymmetryValue)
          ?.filter((item) => item !== null && item !== undefined)
      : [];
  // graphData?.filter(item => graphDomain ? item.xAxisValue >= graphDomain[0] && item.xAxisValue <= graphDomain[1] : item)
  //   ?.map((item) => item.metricAsymmetryValue)
  //   .filter((item) => item !== null && item !== undefined);

  const leftFoot = metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" && {
      hasData: leftFootData && leftFootData.length > 0,
      max: leftFootData?.length > 0 ? Math?.max(...leftFootData) : 0,
      mean: leftFootData?.reduce((a, b) => a + b, 0) / leftFootData?.length,
      min: leftFootData?.length > 0 ? Math?.min(...leftFootData) : 0,
    };
  const rightFoot = metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" && {
      hasData: rightFootData && rightFootData.length > 0,
      max: rightFootData?.length > 0 ? Math?.max(...rightFootData) : 0,
      mean: rightFootData?.reduce((a, b) => a + b, 0) / rightFootData?.length,
      min: rightFootData?.length > 0 ? Math?.min(...rightFootData) : 0,
    };
  const meanFoot = metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" && {
      hasData: meanFootData && meanFootData.length > 0,
      max: meanFootData?.length > 0 ? Math?.max(...meanFootData) : 0,
      mean: meanFootData?.reduce((a, b) => a + b, 0) / meanFootData?.length,
      min: meanFootData?.length > 0 ? Math?.min(...meanFootData) : 0,
    };
  const symmetryFoot = metricChartType !== "pie" &&
    metricChartType !== "bullet" &&
    metricChartType !== "images" && {
      hasData: symmetryFootData && symmetryFootData.length > 0,
      max:
        symmetryFootData?.length > 0
          ? Math?.max(...symmetryFootData.map((a) => Math.abs(a)))
          : 0,
      mean:
        symmetryFootData?.reduce((a, b) => a + b, 0) > 0
          ? symmetryFootData?.reduce((a, b) => a + b, 0) /
            symmetryFootData?.length
          : (symmetryFootData?.reduce((a, b) => a + b, 0) /
              symmetryFootData?.length) *
            -1,
      min:
        symmetryFootData?.length > 0
          ? Math?.min(...symmetryFootData.map((a) => Math.abs(a)))
          : 0,
    };

  //combined chart dat
  const combinedData =
    metricChartType === "composed"
      ? {
          [combinedMainKeys?.[0]]:
            metricChartType !== "pie" &&
            graphData
              ?.filter((item) =>
                graphDomain
                  ? item.xAxisValue >= graphDomain[0] &&
                    item.xAxisValue <= graphDomain[1]
                  : item
              )
              ?.map((item) => item?.[combinedMainKeys?.[0]])
              .filter((item) => item !== null && item !== undefined),
          [combinedMainKeys?.[1]]:
            metricChartType !== "pie" &&
            graphData
              ?.filter((item) =>
                graphDomain
                  ? item.xAxisValue >= graphDomain[0] &&
                    item.xAxisValue <= graphDomain[1]
                  : item
              )
              ?.map((item) => item?.[combinedMainKeys?.[1]])
              .filter((item) => item !== null && item !== undefined),
          [metricPrimaryKey]:
            metricChartType !== "pie" &&
            graphData
              ?.filter((item) =>
                graphDomain
                  ? item.xAxisValue >= graphDomain[0] &&
                    item.xAxisValue <= graphDomain[1]
                  : item
              )
              ?.map((item) => item?.[metricPrimaryKey])
              .filter((item) => item !== null && item !== undefined),
        }
      : {};
  const combinedDataForAverages =
    metricChartType === "composed"
      ? {
          [combinedMainKeys?.[0]]: {
            hasData:
              combinedData[combinedMainKeys?.[0]] &&
              combinedData[combinedMainKeys?.[0]].length > 0,
            max:
              combinedData[combinedMainKeys?.[0]]?.length > 0
                ? Math?.max(...combinedData[combinedMainKeys?.[0]])
                : 0,
            mean:
              combinedData[combinedMainKeys?.[0]]?.reduce((a, b) => a + b, 0) /
              combinedData[combinedMainKeys?.[0]]?.length,
            min:
              combinedData[combinedMainKeys?.[0]]?.length > 0
                ? Math?.min(...combinedData[combinedMainKeys?.[0]])
                : 0,
          },
          [combinedMainKeys?.[1]]: {
            hasData:
              combinedData[combinedMainKeys?.[1]] &&
              combinedData[combinedMainKeys?.[1]].length > 0,
            max:
              combinedData[combinedMainKeys?.[1]]?.length > 0
                ? Math?.max(...combinedData[combinedMainKeys?.[1]])
                : 0,
            mean:
              combinedData[combinedMainKeys?.[1]]?.reduce((a, b) => a + b, 0) /
              combinedData[combinedMainKeys?.[1]]?.length,
            min:
              combinedData[combinedMainKeys?.[1]]?.length > 0
                ? Math?.min(...combinedData[combinedMainKeys?.[1]])
                : 0,
          },
          [metricPrimaryKey]: {
            hasData:
              combinedData[metricPrimaryKey] &&
              combinedData[metricPrimaryKey].length > 0,
            max:
              combinedData[metricPrimaryKey]?.length > 0
                ? Math?.max(...combinedData[metricPrimaryKey])
                : 0,
            mean:
              combinedData[metricPrimaryKey]?.reduce((a, b) => a + b, 0) /
              combinedData[metricPrimaryKey]?.length,
            min:
              combinedData[metricPrimaryKey]?.length > 0
                ? Math?.min(...combinedData[metricPrimaryKey])
                : 0,
          },
        }
      : {};

  useEffect(() => {
    if (metricChartType === "pie") {
      const formattedData = prepareDataForPieMetric(
        data,
        mainDataKeys,
        metricPrimaryKey,
        metricSecondaryKey,
        xAxisValue
      );

      setGraphData(formattedData);
    }
    if (
      metricChartType !== "pie" &&
      metricChartType !== "composed" &&
      metricChartType !== "images" &&
      metricChartType !== "bullet"
    ) {
      const formattedData = prepareDataForMetric(
        data,
        bilateral,
        hasBilateralAsymmetry,
        hasAsymmetry,
        mainDataKeys,
        secondaryDataKeys,
        asymmetryDataKeys,
        bilateralAsymmetryKeys,
        metricPrimaryKey,
        xAxisValue,
        metricSecondaryKey,
        toggleGraph,
        isJump
      );

      setGraphData(formattedData);
    }

    if (metricChartType === "composed") {
      const formattedData = preparedDataForCombinedMetric(
        data,
        hasCombinedKeys,
        metricPrimaryKey,
        combinedMainKeys,
        mainDataKeys,
        asymmetryDataKeys
      );

      setGraphData(formattedData);
    }

    if (metricChartType === "bullet") {
      const formattedData = perpareDataForBulletChart(
        data,
        metricPrimaryKey,
        hasCards,
        cardKeys,
        mainDataKeys,
        secondaryDataKeys
      );
      setGraphData(formattedData);
    }

    if (metricChartType === "images") {
      const formattedData = fullData?.multimediaData;

      setGraphData(formattedData);
    }
  }, [props, toggleGraph, selectedActivityType]);

  const isAllowed = (forDevOnly) => {
    const isDevEnvironment = process.env.REACT_APP_API_HOST.includes("dev");

    if (forDevOnly && isDevEnvironment) {
      return true;
    } else if (!forDevOnly) {
      return true;
    } else {
      return false;
    }
  };

  const CustomTooltipForLineCharts = ({ active, payload, label, data }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          {data.length && label && (
            <div className="label">
              {modifyTimeString(xAxisLabel, data.length)} :{" "}
              {`${formatTime(label, data[data.length - 2])}`}
            </div>
          )}

          {payload.length >= 1 && (
            <div className={`${getTooltipStyle(payload[0])}`}>
              {`${payload[0].name}`} :{" "}
              {` ${Math.round(payload[0].value * 100) / 100}`}{" "}
              {!toggleGraph ? metricUnit : metricSecondaryUnit}{" "}
            </div>
          )}
          {payload.length >= 2 && (
            <div className={`${getTooltipStyle(payload[1])}`}>
              {`${payload[1].name}`} :{" "}
              {` ${Math.round(payload[1].value * 100) / 100}`}{" "}
              {!toggleGraph ? metricUnit : metricSecondaryUnit}{" "}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const CustomTooltipForComposedCharts = ({ active, payload, label, data }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          {data.length && label && (
            <div className="label">
              {modifyTimeString(xAxisLabel, data.length)} :{" "}
              {`${formatTime(label, data[data.length - 2])}`}
            </div>
          )}

          {payload.map((item) => (
            <div style={{ color: item.color }}>
              {`${item.name}`} : {` ${Math.round(item.value * 100) / 100}`} {}{" "}
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const CustomTooltipForAsymmetry = ({ active, payload }) => {
    if (active && payload && payload.length) {
      let className = "right";
      let display = "";

      let displayValue = Math.round(payload[0].value * 100) / 100;
      if (payload[0].value > 0) {
        display = "Right Foot";
        className = "right";
      } else {
        displayValue = (Math.round(payload[0].value * 100) / 100) * -1;
        display = "Left Foot";
        className = "left";
      }

      return (
        <div className="custom-tooltip">
          <div className={className}>
            {`${display}`} : {` ${displayValue}`}
          </div>
        </div>
      );
    }

    return null;
  };

  const averageKeyValue = (data, key) => {
    let totalSum = 0;
    let count = 0;

    data?.forEach((obj) => {
      if (obj.hasOwnProperty(key)) {
        totalSum += obj[key];
        count++;
      }
    });

    return count > 0 ? totalSum / count : 0;
  };

  const isImageComponetAndHasCorrectData = (graphData, metricType) => {
    if (metricType !== "images") {
      return true;
    }
    if (Object.keys(graphData || {}).length >= 6) {
      return true;
    } else {
      return false;
    }
  };

  if (hasAsymmetry || hasBilateralAsymmetry) {
    const gradientOffset = () => {
      if (Array.isArray(graphData) && graphData.length > 0) {
        const dataMax = Math.max(
          ...graphData
          ?.filter((item) =>
            graphDomain
              ? item.eventStart >= graphDomain[0] &&
                item.eventEnd <= graphDomain[1]
              : true
          )
          .map((i) => i["metricAsymmetryValue"])
        );
        const dataMin = Math.min(
          ...graphData
          ?.filter((item) =>
            graphDomain
              ? item.eventStart >= graphDomain[0] &&
                item.eventEnd <= graphDomain[1]
              : true
          )
          .map((i) => i["metricAsymmetryValue"])
        );

        if (dataMax <= 0) {
          return 0;
        } else if (dataMin >= 0) {
          return 1;
        } else {
          return dataMax / (dataMax - dataMin);
        }
      }
      return 0; // Return a default value when graphData is not an array or is empty
    };

    offset = gradientOffset();
  }
  if (!disabledGraphIDs.includes(metricID)) {
    return (graphData &&
      isImageComponetAndHasCorrectData(graphData, metricChartType)) ||
      (metricChartType === "pie" &&
        graphData &&
        graphData.left[0].value &&
        graphData.left[1].value &&
        graphData.right[0].value &&
        graphData.right[1].value) ? (
      isAllowed(forDevOnly) ? (
        metricChartType === "pie" ||
        checkKeysForTruthyValues(
          graphData,
          "metricLeftValue",
          "metricRightValue",
          "dutyFactor",
          "metricMeanValue",
          metricPrimaryKey
        ) ||
        isSuperAdmin ||
        isImageComponetAndHasCorrectData(graphData, metricChartType) ? (
          <div
            className="chart_container toggle-metric-class"
            key={key}
            id={metricID}
          >
            {/* metric name start ----------------------------------- */}
            <div className="metric_name">
              <div className="metric_title">
                {toggleableMetric && (
                  <h3>
                    {!toggleGraph
                      ? metricPrimaryGraphName
                      : metricSecondaryGraphName}
                  </h3>
                )}
                {!toggleableMetric && (
                  <h3>
                    {metricName}
                    {!toggleGraph
                      ? metricUnit
                        ? ` [${metricUnit}]`
                        : ``
                      : metricSecondaryUnit
                      ? ` [${metricSecondaryUnit}]`
                      : ``}
                  </h3>
                )}
                {metricChartType !== "pie" ? (
                  <MainTooltip
                    tooltipText={
                      !toggleGraph
                        ? metricPrimaryDescription
                        : metricSecondaryDescription
                    }
                    iconSize={22}
                  />
                ) : (
                  <MainTooltip
                    tooltipText={
                      metricPrimaryDescription +
                      ". " +
                      metricSecondaryDescription
                    }
                    iconSize={22}
                  />
                )}
                {inDevelopment ? (
                  <MainTooltip
                    tooltipText={"This metric is in Development"}
                    iconSize={22}
                    iconType={"circle"}
                    tooltipIcon={"warning"}
                    iconColor={NEW_BOTH_FEET_COLOR}
                  />
                ) : null}

                {forDevOnly ? (
                  <MainTooltip
                    tooltipText={
                      "This metric is only available on the development client."
                    }
                    iconSize={22}
                    tooltipIcon={"development"}
                    iconColor={NEW_BOTH_FEET_COLOR}
                  />
                ) : null}

                {!checkKeysForTruthyValues(
                  graphData,
                  "metricLeftValue",
                  "metricRightValue",
                  "dutyFactor",
                  "metricMeanValue",
                  metricPrimaryKey
                ) &&
                isSuperAdmin &&
                metricChartType !== "pie" &&
                (metricChartType !== "images") &
                  (metricChartType !== "bullet") ? (
                  <MainTooltip
                    tooltipText={`This Metric is usually available for this activity, this session is missing the data necessary to generate the graph. (note: This tooltip is only visible to Super Admin Users)`}
                    iconSize={22}
                    iconType={"triangle"}
                    tooltipIcon={"warning"}
                    iconColor={"red"}
                  />
                ) : null}
              </div>
              {toggleableMetric ? (
                <div className="metric_toggler">
                  <Toggler
                    isToggled={toggleGraph}
                    setIsToggled={setToggleGraph}
                    changeToggleDisplayed={() => {
                      
                    }}
                    togglerHeadings={{
                      left: toggleOptions[0],
                      right: toggleOptions[1],
                    }}
                  />
                </div>
              ) : null}
            </div>
            {/* metric name end ----------------------------------- */}

            {metricChartType === "line" &&
            checkKeysForTruthyValues(
              graphData,
              "metricLeftValue",
              "metricRightValue",
              "dutyFactor",
              "metricMeanValue",
              metricPrimaryKey
            ) ? (
              <div className="metric_container">
                <div className="metric_chart">
                  <div
                    className={`${
                      hasAsymmetry
                        ? "metric_chart_main_with_asym"
                        : "metric_chart_main_w_out_asym"
                    } ${
                      !asymmetryToggled
                        ? "metric_chart_main_asymmetry_toggled"
                        : ""
                    }`}
                  >
                    <div className="metric_values">
                      <MaxMinMean leftFoot={leftFoot} rightFoot={rightFoot} />
                    </div>
                    <div className="raw_metric_container">
                      <ResponsiveContainer>
                        <ComposedChart
                          data={filterByTimeRange(graphDomain, graphData, "LineCHart")}
                          margin={{
                            top: 5,
                            right: 10,
                            left: 0,
                            bottom: 5,
                          }}
                          syncId={"ChartTest"}
                        >
                          <XAxis
                            label={{
                              value: modifyTimeString(
                                xAxisLabel,
                                graphData?.length
                              ),
                              position: "bottom",
                              fill: tickColor,
                              offset: -6,
                            }}
                            dataKey="xAxisValue"
                            interval={0}
                            tick={{ fill: tickColor }}
                            tickFormatter={(value) =>
                              formatTime(value, graphData[graphData.length - 2])
                            }
                            stroke={NEW_AXIS_COLOR}
                            type="number"
                            allowDataOverflow
                            domain={
                              graphDomain && !isTrimOpen ? graphDomain : [0]
                            }
                          />
                          <YAxis
                            label={{
                              value: !toggleGraph
                                ? yAxisLabel
                                : yAxisSecondaryLabel,
                              angle: -90,
                              position: "insideLeft",
                              fill: tickColor,
                              fontSize: "12px",
                              style: { textAnchor: "middle" },
                            }}
                            tick={{ fill: tickColor }}
                            stroke={NEW_AXIS_COLOR}
                          />
                          {trimDomain &&
                            isTrimOpen &&
                            mappingTrimAreaX(trimDomain)}
                          <>
                            <Line
                              name="Left Foot"
                              dataKey={"metricLeftValue"}
                              stroke={NEW_LEFT_COLOR}
                              opacity={1}
                              connectNulls={true}
                              dot={graphData?.length > 70 ? false : true}
                              isAnimationActive={false}
                            />

                            <Line
                              name="Right Foot"
                              dataKey={"metricRightValue"}
                              stroke={NEW_RIGHT_COLOR}
                              opacity={1}
                              connectNulls={true}
                              dot={graphData?.length > 70 ? false : true}
                              isAnimationActive={false}
                            />
                          </>

                          <Tooltip
                            content={
                              <CustomTooltipForLineCharts data={graphData} />
                            }
                          />

                          <CartesianGrid
                            strokeDasharray="4 4"
                            stroke={axisColor}
                            strokeWidth={1}
                            opacity={0.4}
                          />

                          <Legend
                            verticalAlign="top"
                            align="right"
                            layout="horizontal"
                            wrapperStyle={{ top: 0, fontSize: 11 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  {hasAsymmetry ? (
                    <div
                      className={`metric_chart_asymmetry ${
                        !asymmetryToggled
                          ? "metric_chart_asymmetry_toggled"
                          : ""
                      }`}
                    >
                      <div
                        className={`metric_chart_toggler ${
                          asymmetryToggled ? "asym_chart_toggled" : ""
                        }`}
                        onClick={() => setAsymmetryToggled((prev) => !prev)}
                      >
                        <Polygon />
                      </div>
                      <div className="raw_metric_container_asym">
                        <div className="metric_values">
                          <MaxMinMean footSymmetry={symmetryFoot} />
                        </div>
                        <div className="metric_container_asym">
                          <ResponsiveContainer>
                            <ComposedChart
                              data={filterByTimeRange(graphDomain, graphData, "Asymmetry")}
                              margin={{
                                top: 5,
                                right: 5,
                                left: 0,
                                bottom: 5,
                              }}
                              stackOffset="sign"
                              layout="vertical"
                              syncId={"ChartTest"}
                            >
                              <defs>
                                <linearGradient id={asymmID} x1="1" x2="0">
                                  <stop
                                    offset={offset}
                                    stopColor={NEW_RIGHT_COLOR}
                                  />
                                  <stop
                                    offset={offset}
                                    stopColor={NEW_LEFT_COLOR}
                                  />
                                </linearGradient>
                              </defs>

                              <XAxis
                                type="number"
                                domain={chartXdomainScale(symmetryFootData)}
                                tick={{ fill: NEW_AXIS_TICK }}
                                stroke={NEW_AXIS_COLOR}
                                label={{
                                  value: "% Asymmetry",
                                  offset: -3,
                                  position: "insideBottom",
                                  fill: tickColor,
                                }}
                              />
                              <YAxis
                                type="number"
                                label={{
                                  value: modifyTimeString(
                                    xAxisLabel,
                                    graphData?.length
                                  ),
                                  angle: -90,
                                  position: "insideCenter",
                                  fill: tickColor,
                                  fontSize: "12px",
                                  style: { textAnchor: "middle" },
                                }}
                                allowDataOverflow={true}
                                dataKey="xAxisValue"
                                tick={{ fill: tickColor, fontSize: "10px" }}
                                stroke={NEW_AXIS_COLOR}
                                domain={
                                  graphDomain && !isTrimOpen ? graphDomain : [0]
                                }
                                tickFormatter={(value) =>
                                  formatTime(
                                    value,
                                    graphData[graphData.length - 2]
                                  )
                                }
                              />
                              <ReferenceLine
                                x={0}
                                stroke={tickColor}
                                strokeDasharray="5 5"
                              />
                              {trimDomain &&
                                isTrimOpen &&
                                mappingTrimAreaY(trimDomain)}
                              <>
                                <Line
                                  dataKey="metricAsymmetryValue"
                                  stroke={`url(#${asymmID})`}
                                  dot={graphData?.length > 70 ? false : true}
                                  connectNulls={true}
                                  isAnimationActive={false}
                                />
                              </>

                              <Tooltip
                                content={<CustomTooltipForAsymmetry />}
                              />

                              <CartesianGrid
                                strokeDasharray="4 4"
                                stroke={axisColor}
                                strokeWidth={1}
                                opacity={0.4}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {metricChartType === "composed" &&
            checkKeysForTruthyValues(
              graphData,
              metricPrimaryKey,
              combinedMainKeys?.[0],
              combinedMainKeys?.[1],
              "metricMeanValue"
            ) ? (
              <div className="metric_container">
                <div className="metric_chart">
                  <div
                    className={`${
                      hasAsymmetry
                        ? "metric_chart_main_with_asym"
                        : "metric_chart_main_w_out_asym"
                    } ${
                      !asymmetryToggled
                        ? "metric_chart_main_asymmetry_toggled"
                        : ""
                    }`}
                  >
                    <div className="metric_values">
                      <MaxMinMean
                        rightFoot={
                          combinedDataForAverages[combinedMainKeys?.[0]]
                        }
                        leftFoot={
                          combinedDataForAverages[combinedMainKeys?.[1]]
                        }
                        bothFeet={combinedDataForAverages[metricPrimaryKey]}
                      />
                    </div>
                    <div className="raw_metric_container">
                      <ResponsiveContainer>
                        <ComposedChart
                          data={graphData}
                          margin={{
                            top: 5,
                            right: 10,
                            left: 0,
                            bottom: 5,
                          }}
                          syncId={"ChartTest"}
                        >
                          <XAxis
                            label={{
                              value: !toggleGraph
                                ? modifyTimeString(
                                    xAxisLabel,
                                    graphData?.length
                                  )
                                : xAxisSecondaryLabel,
                              position: "bottom",
                              fill: tickColor,
                              offset: -6,
                            }}
                            dataKey="xAxisValue"
                            interval={0}
                            tick={{ fill: tickColor }}
                            stroke={NEW_AXIS_COLOR}
                            type="number"
                            allowDataOverflow
                            domain={
                              graphDomain && !isTrimOpen ? graphDomain : [0]
                            }
                            tickFormatter={(value) =>
                              formatTime(value, graphData[graphData.length - 2])
                            }
                          />
                          <YAxis
                            yAxisId="left"
                            orientation="left"
                            label={{
                              value: !toggleGraph
                                ? yAxisLabel
                                : yAxisSecondaryLabel,
                              angle: -90,
                              position: "insideLeft",
                              fill: tickColor,
                              fontSize: "12px",
                              style: { textAnchor: "middle" },
                            }}
                            tick={{ fill: tickColor }}
                            stroke={NEW_AXIS_COLOR}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            label={{
                              value: metricName,
                              angle: -90,
                              position: "outsideLeft",
                              fill: tickColor,
                              style: { textAnchor: "middle" },
                            }}
                            tick={{ fill: tickColor }}
                            stroke={NEW_AXIS_COLOR}
                          />
                          {trimDomain &&
                            isTrimOpen &&
                            mappingTrimAreaX(trimDomain)}
                          {hasCombinedKeys ? (
                            <>
                              <Line
                                yAxisId="left"
                                name={metricSecondaryMetricName}
                                dataKey={combinedMainKeys?.[0]}
                                stroke={NEW_RIGHT_COLOR}
                                opacity={1}
                                connectNulls={true}
                                dot={graphData?.length > 70 ? false : true}
                                isAnimationActive={false}
                              />
                              <Line
                                yAxisId="left"
                                name={metricTertiaryMetricName}
                                dataKey={combinedMainKeys?.[1]}
                                stroke={NEW_LEFT_COLOR}
                                opacity={1}
                                connectNulls={true}
                                dot={graphData?.length > 70 ? false : true}
                                isAnimationActive={false}
                              />

                              <Bar
                                yAxisId="right"
                                data={graphData}
                                dataKey={metricPrimaryKey}
                                barSize={4}
                                name="Drive Index"
                                fill={NEW_MID_COLOR}
                                opacity={0.6}
                              />
                              <Bar
                                yAxisId="right"
                                dataKey={metricPrimaryKey}
                                legendType="none"
                                tooltipType="none"
                                fill="transparent"
                              />
                            </>
                          ) : (
                            <>
                              <Line
                                yAxisId="left"
                                name={metricPrimaryName}
                                dataKey={metricPrimaryKey}
                                stroke={NEW_BOTH_FEET_COLOR}
                                opacity={1}
                                connectNulls={true}
                                dot={graphData?.length > 70 ? false : true}
                                isAnimationActive={false}
                              />
                            </>
                          )}

                          <Tooltip
                            content={
                              <CustomTooltipForComposedCharts
                                data={graphData}
                              />
                            }
                          />

                          <CartesianGrid
                            strokeDasharray="4 4"
                            stroke={gridColor}
                            strokeWidth={1}
                            opacity={0.4}
                          />

                          <Legend
                            verticalAlign="top"
                            align="right"
                            layout="horizontal"
                            wrapperStyle={{ top: 0, fontSize: 11 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {metricChartType === "bar" &&
            checkKeysForTruthyValues(
              graphData,
              "metricLeftValue",
              "metricRightValue",
              "dutyFactor",
              "metricMeanValue",
              metricPrimaryKey
            ) ? (
              <div className="metric_container">
                <div className="metric_chart">
                  <div
                    className={`${
                      ((hasAsymmetry || hasBilateralAsymmetry) &&
                        toggleBilateralGraph) ||
                      !hasBilateralToggler
                        ? "metric_chart_main_with_asym"
                        : "metric_chart_main_w_out_asym"
                    } ${
                      !asymmetryToggled
                        ? "metric_chart_main_asymmetry_toggled"
                        : ""
                    }`}
                  >
                    <div className="metric_values">
                      {toggleBilateralGraph || !hasBilateralToggler ? (
                        <MaxMinMean leftFoot={leftFoot} rightFoot={rightFoot} />
                      ) : (
                        <MaxMinMean footSymmetry={meanFoot} />
                      )}
                      {hasBilateralToggler ? (
                        <div className="toggler_for_asym">
                          <Toggler
                            isToggled={toggleBilateralGraph}
                            setIsToggled={setToggleBailateralGraph}
                            changeToggleDisplayed={() => {
                              
                            }}
                            togglerHeadings={{
                              left: bilateralToggleOptions[0],
                              right: bilateralToggleOptions[1],
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="raw_metric_container">
                      <ResponsiveContainer>
                        <BarChart
                          data={filterByTimeRange(graphDomain, graphData)}
                          margin={{
                            top: 5,
                            right: 10,
                            left: 0,
                            bottom: 5,
                          }}
                          stackOffset="sign"
                          syncId={"ChartTest"}
                        >
                          <CartesianGrid
                            strokeDasharray="4 4"
                            stroke={gridColor}
                            vertical={false}
                            strokeWidth={1}
                          />
                          <XAxis
                            label={{
                              value: !toggleGraph
                                ? xAxisLabel
                                : xAxisSecondaryLabel,
                              position: "bottom",
                              fill: tickColor,
                              offset: -7,
                            }}
                            dataKey={hasMultipleActivities ? "xAxisValueCat" : "xAxisValue"}
                            tick={{ fill: tickColor }}
                            stroke={NEW_AXIS_COLOR}
                            type="category"
                            allowDataOverflow
                            domain={
                              graphDomain && !isTrimOpen ? graphDomain : [0]
                            }
                          />
                          <YAxis
                            label={{
                              value: !toggleGraph
                                ? yAxisLabel
                                : yAxisSecondaryLabel,
                              angle: -90,
                              position: "insideLeft",
                              fill: tickColor,
                              fontSize: "12px",
                              style: { textAnchor: "middle" },
                            }}
                            tick={{ fill: tickColor }}
                            stroke={NEW_AXIS_COLOR}
                          />
                          <Tooltip
                            cursor={{ fill: gridColor, opacity: 0.6 }}
                            content={
                              <CustomTooltipForJumpCharts
                                desc={
                                  !toggleGraph
                                    ? metricUnit
                                    : metricSecondaryUnit
                                }
                                data={graphData}
                              />
                            }
                          />

                          {toggleBilateralGraph || !hasBilateralToggler ? (
                            <>
                              <Bar
                                dataKey={"metricLeftValue"}
                                fill={NEW_LEFT_COLOR}
                                name="Left Foot"
                                barSize={90}
                                opacity={0.9}
                              />
                              <Bar
                                dataKey={"metricRightValue"}
                                fill={NEW_RIGHT_COLOR}
                                name="Right Foot"
                                barSize={90}
                                opacity={0.9}
                              />
                            </>
                          ) : (
                            <Bar
                              dataKey={"metricMeanValue"}
                              fill={NEW_RIGHT_COLOR}
                              name="Both Feet"
                              barSize={90}
                              opacity={0.9}
                            />
                          )}

                          {toggleBilateralGraph || !hasBilateralToggler ? (
                            <>
                              <ReferenceLine
                                stroke={NEW_LEFT_COLOR}
                                strokeDasharray="5 5"
                                y={averageKeyValue(
                                  graphData,
                                  "metricLeftValue"
                                )}
                                strokeWidth={2}
                              />
                              <ReferenceLine
                                stroke={NEW_RIGHT_COLOR}
                                strokeDasharray="5 5"
                                y={averageKeyValue(
                                  graphData,
                                  "metricRightValue"
                                )}
                                strokeWidth={2}
                              />
                            </>
                          ) : (
                            <ReferenceLine
                              stroke={NEW_RIGHT_COLOR}
                              strokeDasharray="5 5"
                              y={averageKeyValue(graphData, "metricMeanValue")}
                              strokeWidth={2}
                            />
                          )}

                          <Legend
                            verticalAlign="top"
                            align="right"
                            layout="horizontal"
                            wrapperStyle={{ top: 0, fontSize: 11 }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  {hasAsymmetry ||
                  (hasBilateralAsymmetry && toggleBilateralGraph) ? (
                    <div
                      className={`metric_chart_asymmetry ${
                        !asymmetryToggled
                          ? "metric_chart_asymmetry_toggled"
                          : ""
                      }`}
                    >
                      <div
                        className={`metric_chart_toggler ${
                          asymmetryToggled ? "asym_chart_toggled" : ""
                        }`}
                        onClick={() => setAsymmetryToggled((prev) => !prev)}
                      >
                        <Polygon />
                      </div>
                      <div className="raw_metric_container_asym">
                        <div className="metric_values">
                          <MaxMinMean footSymmetry={symmetryFoot} />
                        </div>
                        <div className="metric_container_asym">
                          <ResponsiveContainer>
                            <ComposedChart
                              data={filterByTimeRange(graphDomain, graphData)}
                              margin={{
                                top: 5,
                                right: 5,
                                left: 0,
                                bottom: 5,
                              }}
                              stackOffset="sign"
                              layout="vertical"
                              syncId={"ChartTest"}
                            >
                              <ReferenceLine
                                x={0}
                                stroke={tickColor}
                                strokeDasharray="5 5"
                              />
                              <XAxis
                                type="number"
                                domain={chartXdomainScale(symmetryFootData)}
                                tick={{ fill: tickColor }}
                                stroke={NEW_AXIS_COLOR}
                                label={{
                                  value: "% Asymmetry",
                                  offset: -3,
                                  position: "insideBottom",
                                  fill: tickColor,
                                }}
                              />
                              <YAxis
                                type="category"
                                label={{
                                  value: "Reps",
                                  angle: -90,
                                  position: "insideCenter",
                                  fill: tickColor,
                                  fontSize: "12px",
                                  style: { textAnchor: "middle" },
                                }}
                                dataKey={hasMultipleActivities ? "xAxisValueCat" : "xAxisValue"}
                                tick={{ fill: tickColor, fontSize: "10px" }}
                                stroke={NEW_AXIS_COLOR}
                              />
                              <CartesianGrid
                                strokeDasharray="4 4"
                                stroke={gridColor}
                                strokeWidth={1}
                                opacity={0.4}
                              />
                              <Tooltip
                                cursor={{ fill: tickColor, opacity: 0.6 }}
                                content={<CustomTooltipForAsymmetry />}
                              />
                              {trimDomain &&
                                isTrimOpen &&
                                mappingTrimAreaY(trimDomain)}
                              <>
                                <Bar
                                  dataKey="metricAsymmetryValue"
                                  opacity={1}
                                  barSize={50}
                                >
                                  {graphData
                                    ?.filter((item) =>
                                      graphDomain
                                        ? item.eventStart >= graphDomain[0] &&
                                          item.eventEnd <= graphDomain[1]
                                        : true
                                    )
                                    ?.map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={
                                          entry["metricAsymmetryValue"] < 0
                                            ? NEW_LEFT_COLOR
                                            : NEW_RIGHT_COLOR
                                        }
                                        connectNulls={true}
                                      />
                                    ))}
                                </Bar>
                              </>
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {metricChartType === "pie" ? (
              <div className="metric_container">
                <div className="raw_metric_pie_charts">
                  {graphData &&
                    renderPieChart(graphData.left, metricPrimaryName, "Left")}
                  {graphData &&
                    renderPieChart(
                      graphData.right,
                      metricSecondaryName,
                      "Right"
                    )}
                </div>
              </div>
            ) : null}

            {metricChartType === "bullet" ? (
              <div className="metric_container">
                <div className="metric_container">
                  <div className="metric_chart">
                    <div
                      className={`${
                        hasCards
                          ? "metric_chart_main_with_asym"
                          : "metric_chart_main_w_out_asym"
                      } ${
                        !asymmetryToggled
                          ? "metric_chart_main_asymmetry_toggled"
                          : ""
                      }`}
                    >
                      <div className="main-metric-chart-value">
                        {!toggleGraph && bilateral ? (
                          <h4>
                            {graphData?.[metricPrimaryKey]?.[mainDataKeys[0]]}
                          </h4>
                        ) : (
                          <div>
                            <h4>
                              {
                                graphData?.[metricPrimaryKey]?.[
                                  secondaryDataKeys[0]
                                ]
                              }
                              <span className="span-left">L</span>
                            </h4>
                            <h4>
                              {
                                graphData?.[metricPrimaryKey]?.[
                                  secondaryDataKeys[1]
                                ]
                              }
                              <span className="span-right">R</span>
                            </h4>
                          </div>
                        )}
                      </div>

                      <div className="raw_metric_container_bullet">
                        <ResponsiveContainer>
                          <ComposedChart data={graphData} layout={"vertical"}>
                            <XAxis
                              type="number"
                              display={false}
                              domain={[0, 10]}
                              tickCount={11}
                              tick={{ fill: tickColor }}
                              stroke={tickColor}
                            />
                            <YAxis
                              type="category"
                              dataKey={metricPrimaryGraphName}
                              allowDuplicatedCategory={false}
                              axisLine={false}
                              tick={tickColor}
                              width={0}
                            />
                            {!toggleGraph && bilateral ? (
                              <>
                                <defs>
                                  <linearGradient
                                    id="stabilityScoreGradientMean"
                                    x1="1"
                                    x2="0"
                                  >
                                    <stop
                                      offset={"0%"}
                                      stopColor={"rgba(243, 156, 18,1.0)"}
                                    />
                                    <stop
                                      offset={"50%"}
                                      stopColor={"rgba(243, 156, 18,0.5)"}
                                    />
                                    <stop
                                      offset={"100%"}
                                      stopColor={"rgba(243, 156, 18,0.02)"}
                                    />
                                  </linearGradient>
                                </defs>

                                <Bar
                                  data={[
                                    {
                                      name: "Static",
                                      maxValue: 10,
                                      value: graphData[metricPrimaryKey],
                                      valueKey: "meanResults",
                                    },
                                  ]}
                                  dataKey="maxValue"
                                  barSize={30}
                                  fill="url(#stabilityScoreGradientMean)"
                                  stroke={axisColor}
                                  radius={15}
                                  x={0}
                                  y={50}
                                  shape={<CustomBar />}
                                  barCategoryGap="50%"
                                />
                              </>
                            ) : (
                              <>
                                <defs>
                                  <linearGradient
                                    id="stabilityScoreGradientLeft"
                                    x1="1"
                                    x2="0"
                                  >
                                    <stop
                                      offset={"0%"}
                                      stopColor={"rgba(155, 89, 182,1.0)"}
                                    />
                                    <stop
                                      offset={"50%"}
                                      stopColor={"rgba(155, 89, 182,0.5)"}
                                    />
                                    <stop
                                      offset={"100%"}
                                      stopColor={"rgba(155, 89, 182,0.2)"}
                                    />
                                  </linearGradient>

                                  <linearGradient
                                    id="stabilityScoreGradientRight"
                                    x1="1"
                                    x2="0"
                                  >
                                    <stop
                                      offset={"0%"}
                                      stopColor={"rgba(52, 152, 219,1.0)"}
                                    />
                                    <stop
                                      offset={"50%"}
                                      stopColor={"rgba(52, 152, 219,0.5)"}
                                    />
                                    <stop
                                      offset={"100%"}
                                      stopColor={"rgba(52, 152, 219,0.02)"}
                                    />
                                  </linearGradient>
                                </defs>
                                {/* <Bar /> */}
                                <Line />

                                <Bar
                                  data={[
                                    {
                                      name: "Static",
                                      leftMaxValue: 10,
                                      value: graphData[metricPrimaryKey],
                                      valueKey: "leftResults",
                                    },
                                  ]}
                                  dataKey="leftMaxValue"
                                  barSize={30}
                                  fill="url(#stabilityScoreGradientLeft)"
                                  stroke={axisColor}
                                  radius={15}
                                  x={0}
                                  y={50}
                                  shape={<CustomBar />}
                                />

                                <Bar
                                  data={[
                                    {
                                      name: "Static",
                                      rightMaxValue: 10,
                                      value: graphData[metricPrimaryKey],
                                      valueKey: "rightResults",
                                    },
                                  ]}
                                  dataKey="rightMaxValue"
                                  barSize={30}
                                  fill="url(#stabilityScoreGradientRight)"
                                  stroke={axisColor}
                                  radius={15}
                                  x={0}
                                  y={50}
                                  shape={<CustomBar />}
                                />
                              </>
                            )}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    {hasCards ? (
                      <div
                        className={`metric_chart_cards ${
                          !asymmetryToggled ? "metric_chart_cards_toggled" : ""
                        }`}
                      >
                        <div
                          className={`metric_chart_toggler ${
                            asymmetryToggled ? "asym_chart_toggled" : ""
                          }`}
                          onClick={() => setAsymmetryToggled((prev) => !prev)}
                        >
                          <Polygon />
                        </div>
                        <div className="raw_metric_container_cards">
                          {cardHeadings.map((item, index) => (
                            <div className="card" key={"card" + index}>
                              <div className="card_heading">
                                <h4>{item}</h4>
                              </div>
                              <div className="card_metric">
                                {!toggleGraph && bilateral ? (
                                  <div className="metric_mean_value">
                                    <div className="card_mean">
                                      <p className="card_mean_value">
                                        {
                                          graphData?.[cardKeys?.[index]]?.[
                                            mainDataKeys[0]
                                          ]
                                        }
                                      </p>
                                      <p className="card_mean_unit">
                                        {cardUnits?.[index]}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="metric_individual_values">
                                    <div className="card_mean">
                                      <p className="card_mean_value">
                                        {
                                          graphData?.[cardKeys?.[index]]?.[
                                            secondaryDataKeys[0]
                                          ]
                                        }
                                      </p>
                                      <p className="card_mean_unit">
                                        {cardUnits?.[index]}
                                      </p>
                                      <p className="card_foot_symbol_left">L</p>
                                    </div>
                                    <div className="card_mean">
                                      <p className="card_mean_value">
                                        {
                                          graphData?.[cardKeys?.[index]]?.[
                                            secondaryDataKeys[1]
                                          ]
                                        }
                                      </p>
                                      <p className="card_mean_unit">
                                        {cardUnits?.[index]}
                                      </p>
                                      <p className="card_foot_symbol_right">
                                        R
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {metricChartType === "images" &&
            Object.keys(graphData || {})?.length >= 6 ? (
              <div className="metric_container" style={{ marginLeft: "-20px" }}>
                <div className="metric_container_cop">
                  {imageHeadings.map((item, index) => {
                    return (
                      <div
                        key={nanoid()}
                        style={{
                          width: `${100 / imageHeadings.length.toFixed(0)}%`,
                        }}
                      >
                        <COPImagesComponent
                          width={`${100 / imageHeadings.length.toFixed(0)}%`}
                          graphData={graphData}
                          heading={imageHeadings[index]}
                          keys={imageDataKeys[index]}
                          tooltips={imageTooltips[index]}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        ) : null
      ) : null
    ) : null;
  } else {
    return null;
  }
};

export default RawChartComponent;
